/**
 * Configuración (por código) de los recursos en tiempo real y consultas por item
 * de los listados de cargo (cargo-list / cargo-item).
 *
 * Objetivo: reducir el costo por item que escala con el listado (listeners de
 * Firestore y peticiones HTTP por cada cargo renderizado), que degrada el
 * navegador a medida que el usuario pagina y cambia entre listados.
 *
 * Cómo apagar/encender: editar las constantes de este archivo (comentar una
 * línea, poner `false`, etc.). No hay interfaz; el control es por código.
 */

export interface CargoRealtimeFlags {
  /** Listener Firestore sobre el documento Cargo/{id} (auto-refresh de la carga). */
  cargoWatch: boolean;
  /** Listener Firestore sobre la subcolección Cargo/{id}/Payments (auto-refresh de saldo). */
  paymentsWatch: boolean;
}

/**
 * Kill-switch global. En `false` apaga TODO el realtime de cargo-item,
 * independientemente de la configuración por listado de abajo.
 */
export const CARGO_REALTIME_ENABLED = false;

/**
 * Cuando es `true`, la consulta de viáticos (travelExpenses) NO se hace por item
 * al renderizar la lista; se carga bajo demanda al abrir el detalle de saldo.
 * Elimina N peticiones HTTP por página.
 *
 * Trade-off: el saldo mostrado en la fila no descuenta viáticos hasta abrir el
 * detalle; el detalle de saldo sí carga el dato y es exacto.
 */
export const CARGO_TRAVEL_EXPENSES_LAZY = true;

/** Valores por defecto aplicados a cualquier listado sin override explícito. */
export const DEFAULT_CARGO_REALTIME: CargoRealtimeFlags = {
  cargoWatch: false,
  paymentsWatch: false
};

/**
 * Override por tipo de listado (`typeList`). Comentar una línea = ese listado
 * vuelve a usar DEFAULT_CARGO_REALTIME. Se puede definir uno o ambos flags.
 */
export const CARGO_REALTIME_BY_LIST: { [typeList: string]: Partial<CargoRealtimeFlags> } = {
  // loadingRoutes: { cargoWatch: true, paymentsWatch: false }, // necesita seguimiento en vivo, no saldo
  // approvePayments: { cargoWatch: false, paymentsWatch: true }, // ejemplo: solo saldo en vivo
  // scheduledLoads: { cargoWatch: false, paymentsWatch: false },
};

/** Resuelve los flags de realtime efectivos para un `typeList` dado. */
export function resolveCargoRealtime(typeList: string): CargoRealtimeFlags {
  if (!CARGO_REALTIME_ENABLED) {
    return { cargoWatch: false, paymentsWatch: false };
  }
  return { ...DEFAULT_CARGO_REALTIME, ...(CARGO_REALTIME_BY_LIST[typeList] || {}) };
}
